import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import { useWaterSlotRules } from '@/hooks/useWaterSlotRules';
import { cn } from '@/lib/cn';
import { toNumberOrNull } from '@/lib/number';
import { alkalinityTier, calMagTier, formatCalMag, formatPair } from '@/lib/pairs';
import { pairColor, paramColor, waterColorDot, type StatusColor } from '@/lib/paramColor';
import { clearText } from '@/lib/clearToBottom';
import { addPhDays, displayPhDate, phMinutesOfDay, todayInPH } from '@/lib/phDate';
import {
  ALL_SLOTS,
  DO_SLOTS,
  GRID_ROWS,
  OBSERVATION_PARAMS,
  SLOT_META,
  SLOT_PARAMS,
  computePhDial,
  isApplicable,
  paramByKey,
  roundSlotAt,
  type ParamDef,
} from '@/lib/waterModel';
import type { AlertThresholds, DayReadings, DaySlotReading } from '@/types/api';
import type { ReadingSlot } from '@/types/entry';

/**
 * The newest reading day of a pond card (#392): the two full rounds side by side (AM = the first
 * full round, PM = the second), the lab tests that only exist at PM in their own block, and one
 * chip per DO round. Which parameter belongs to which round comes from SLOT_PARAMS / SLOT_META.
 */

type CellState = 'value' | 'due' | 'missing' | 'na' | 'clear';

interface Cell {
  state: CellState;
  text: string;
  color?: StatusColor;
}

const DOT_COLORS: Record<StatusColor, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  gray: 'bg-gray-300',
};
const DOT_TEXT: Partial<Record<StatusColor, string>> = { amber: 'warning', red: 'critical' };

const [AM_SLOT, PM_SLOT] = ALL_SLOTS.filter((s) => SLOT_META[s].type === 'FULL');
const BOTH_ROUNDS = [...GRID_ROWS, ...OBSERVATION_PARAMS].filter(
  (p) => p.key !== 'weather' && SLOT_PARAMS[AM_SLOT].includes(p.key) && SLOT_PARAMS[PM_SLOT].includes(p.key)
);
const PM_ONLY = GRID_ROWS.filter(
  (p) => SLOT_PARAMS[PM_SLOT].includes(p.key) && !SLOT_PARAMS[AM_SLOT].includes(p.key) && p.key !== 'dissolvedOxygen'
);
const DIGITS: Record<string, number> = { dissolvedOxygen: 1, ph: 1, temperature: 1, salinity: 1, depth: 2, ammonia: 2, nitrite: 2, transparency: 0 };
const SUFFIX: Record<string, string> = { temperature: '°C', depth: ' m', transparency: ' cm' };

/**
 * Whether a pond's newest reading day still counts as up to date. Before the first full round of the day
 * (7am) every pond's newest day is yesterday's, and flagging them all would light up the whole farm each
 * morning; from the moment that round is up, a pond with nothing from today has missed it.
 */
function readingsAreCurrent(readingDate: string, today: string, round: number): boolean {
  if (readingDate >= today) return true;
  return readingDate === addPhDays(today, -1) && round < ALL_SLOTS.indexOf(AM_SLOT);
}

const colorWord = (value: string) => paramByKey('waterColor')?.options?.find((o) => o.value === value)?.label ?? value.replace(/_/g, ' ');

function Dot({ color }: { color?: StatusColor }) {
  if (!color) return null;
  return (
    <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full', DOT_COLORS[color])}>
      {DOT_TEXT[color] && <span className="sr-only">{DOT_TEXT[color]}</span>}
    </span>
  );
}

function CellView({ cell, ...rest }: { cell: Cell; 'data-day-cell'?: string; 'data-pm-cell'?: string }) {
  return (
    <div
      {...rest}
      data-cell-state={cell.state}
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-x-1 text-[11px] tabular-nums',
        cell.state === 'value' || cell.state === 'clear' ? 'font-medium text-gray-900' : 'text-gray-400',
        cell.state === 'due' && 'italic',
        cell.state === 'na' &&
          'w-fit rounded bg-[repeating-linear-gradient(135deg,#f3f4f6_0_3px,#e5e7eb_3px_6px)] px-1 text-gray-500'
      )}
    >
      <Dot color={cell.color} />
      <span className="min-w-0 break-words">{cell.text}</span>
    </div>
  );
}

interface Props {
  day: DayReadings;
  alertThresholds?: AlertThresholds | null;
}

export function DayReadingsTable({ day, alertThresholds }: Props) {
  const { table, colorTiers } = useFarmThresholds();
  const slotOff = useWaterSlotRules();
  const isToday = day.reading_date === todayInPH();
  const currentRound = ALL_SLOTS.indexOf(roundSlotAt(phMinutesOfDay()));
  const isCurrent = readingsAreCurrent(day.reading_date, todayInPH(), currentRound);
  const bySlot = new Map<string, DaySlotReading>(day.readings.map((r) => [r.reading_slot, r]));

  const farmBand = (key: string): [number | undefined, number | undefined, number] | undefined => {
    const t = alertThresholds;
    if (!t) return undefined;
    const bands: Record<string, [number | undefined, number | undefined, number]> = {
      ph: [t.ph_min, t.ph_max, 0.5],
      dissolvedOxygen: [t.do_min, t.do_max, 1],
      temperature: [t.temp_min, t.temp_max, 2],
      salinity: [t.salinity_min, t.salinity_max, 2],
      ammonia: [0, t.ammonia_max, 0.2],
      nitrite: [0, t.nitrite_max, 0.2],
    };
    return bands[key];
  };

  /** How a slot with no usable value reads: not measured there, still to come today, or just missing. */
  const absent = (slot: ReadingSlot, key: string, rowExists: boolean): Cell => {
    if (!SLOT_PARAMS[slot].includes(key) || !isApplicable(key, slot, slotOff)) return { state: 'na', text: 'n/a' };
    if (!rowExists && isToday && ALL_SLOTS.indexOf(slot) >= currentRound) return { state: 'due', text: 'due' };
    return { state: 'missing', text: '—' };
  };

  const numberCell = (slot: ReadingSlot, p: ParamDef): Cell => {
    const row = bySlot.get(slot);
    const key = p.key;
    if (!isApplicable(key, slot, slotOff)) return { state: 'na', text: 'n/a' };
    if (key === 'transparency' && row?.transparency_clear_to_bottom) return { state: 'clear', text: clearText(row.depth_m, { unit: false }) };
    const value = toNumberOrNull(row?.[p.dbColumn as keyof DaySlotReading] as number | string | null | undefined);
    if (value === null || value === undefined) return absent(slot, key, !!row);
    const band = farmBand(key);
    return {
      state: 'value',
      text: `${value.toFixed(DIGITS[key] ?? 1)}${SUFFIX[key] ?? ''}`,
      color: paramColor(table, key, value, band?.[0], band?.[1], band?.[2]),
    };
  };

  const colorCell = (slot: ReadingSlot): Cell => {
    const row = bySlot.get(slot);
    if (!isApplicable('waterColor', slot, slotOff)) return { state: 'na', text: 'n/a' };
    if (!row?.water_color) return absent(slot, 'waterColor', !!row);
    return { state: 'value', text: colorWord(row.water_color), color: waterColorDot(colorTiers, row.water_color) };
  };

  const pmOnlyCell = (p: ParamDef): Cell => {
    const row = bySlot.get(PM_SLOT);
    if (!isApplicable(p.key, PM_SLOT, slotOff)) return { state: 'na', text: 'n/a' };
    if (p.key === 'alkalinity') {
      const first = row?.alkalinity_ppm ?? row?.alkalinity_am ?? row?.alkalinity_pm;
      const text = formatPair(first, row?.alkalinity_2);
      if (!text) return absent(PM_SLOT, p.key, !!row);
      const nums = [first, row?.alkalinity_2].map(toNumberOrNull);
      const salinity = toNumberOrNull(row?.salinity_ppt);
      return { state: 'value', text, color: pairColor(table, alkalinityTier(table, nums[0], nums[1], { salinity_ppt: salinity }), true) };
    }
    if (p.key === 'calcium') {
      const text = formatCalMag(row?.calcium_ppm, row?.magnesium_ppm, row?.calmag);
      if (!text) return absent(PM_SLOT, p.key, !!row);
      const nums = [row?.calcium_ppm, row?.magnesium_ppm].map(toNumberOrNull);
      const salinity = toNumberOrNull(row?.salinity_ppt);
      return {
        state: 'value',
        text,
        color: pairColor(table, calMagTier(table, nums[0], nums[1], { salinity_ppt: salinity }), nums.some((n) => n !== null)),
      };
    }
    return numberCell(PM_SLOT, p);
  };

  const doChip = (slot: ReadingSlot): Cell => {
    if (!isApplicable('dissolvedOxygen', slot, slotOff)) return { state: 'na', text: 'n/a' };
    const value = toNumberOrNull(bySlot.get(slot)?.dissolved_oxygen);
    if (value === null || value === undefined) return { state: 'missing', text: '—' };
    const band = farmBand('dissolvedOxygen');
    return { state: 'value', text: value.toFixed(DIGITS.dissolvedOxygen), color: paramColor(table, 'dissolvedOxygen', value, band?.[0], band?.[1], band?.[2]) };
  };

  const dial = computePhDial(
    toNumberOrNull(bySlot.get(AM_SLOT)?.ph) ?? null,
    toNumberOrNull(bySlot.get(PM_SLOT)?.ph) ?? null
  );

  return (
    <div className="space-y-1.5" data-day-table data-day-when={isToday ? 'today' : 'past'}>
      {!isCurrent && (
        <span
          data-last-read
          className="inline-block rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800"
        >
          Last read{' '}
          {(['weekday', 'day', 'month'] as const)
            .map((part) => displayPhDate(day.reading_date, { [part]: part === 'day' ? 'numeric' : 'short' }))
            .join(' ')}
        </span>
      )}

      <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] items-start gap-x-2 gap-y-0.5">
        <span />
        {[AM_SLOT, PM_SLOT].map((slot, i) => (
          <span key={slot} className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {i === 0 ? 'AM' : 'PM'} <span className="font-normal normal-case">{SLOT_META[slot].label}</span>
          </span>
        ))}
        {BOTH_ROUNDS.map((p) => (
          <div key={p.key} data-day-row={p.key} className="contents">
            <span className="text-[11px] text-gray-500">{p.short}</span>
            {[AM_SLOT, PM_SLOT].map((slot, i) => (
              <CellView
                key={slot}
                data-day-cell={i === 0 ? 'am' : 'pm'}
                cell={p.key === 'waterColor' ? colorCell(slot) : numberCell(slot, p)}
              />
            ))}
            {p.key === 'ph' && dial.delta !== null && (
              <>
                <span />
                <span
                  data-ph-dial
                  className={cn('col-span-2 text-[10px]', dial.warning ? 'font-medium text-amber-700' : 'text-gray-500')}
                >
                  pH dial {dial.delta > 0 ? '+' : ''}
                  {dial.delta.toFixed(1)}
                </span>
              </>
            )}
          </div>
        ))}
      </div>

      <div data-pm-only className="border-t border-gray-100 pt-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {SLOT_META[PM_SLOT].label} only
        </p>
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
          {PM_ONLY.map((p) => (
            <div key={p.key} className="flex min-w-0 flex-wrap items-baseline gap-x-1">
              <span className="text-[11px] text-gray-500">{p.short}</span>
              <CellView data-pm-cell={p.key === 'calcium' ? 'calmag' : p.key} cell={pmOnlyCell(p)} />
            </div>
          ))}
        </div>
      </div>

      <div data-do-strip className="border-t border-gray-100 pt-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">DO mg/L</p>
        <div className="grid grid-cols-4 gap-1">
          {DO_SLOTS.map((slot) => {
            const cell = doChip(slot);
            return (
              <div
                key={slot}
                data-do-chip={slot}
                data-cell-state={cell.state === 'missing' ? 'none' : cell.state}
                className={cn(
                  'min-w-0 rounded border px-1 py-0.5 text-center',
                  cell.state === 'value' ? 'border-gray-200 bg-white' : 'border-dashed border-gray-300 bg-gray-50'
                )}
              >
                <span className="block text-[10px] leading-tight text-gray-500">{SLOT_META[slot].label}</span>
                <span className="flex items-center justify-center gap-1 text-[11px] font-medium tabular-nums text-gray-900">
                  <Dot color={cell.color} />
                  <span className={cell.state === 'value' ? '' : 'text-gray-400'}>{cell.text}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
